import React, { useState } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import {
  Security,
  Notifications,
  AccountCircle,
  Brightness4,
  Palette,
  People,
  Block,
  Group,
  CloudDownload,
  Business,
  LocalOffer,
  HeadsetMic,
  Home,
  Menu as MenuIcon,
  ChevronLeft,
  WorkspacePremium,
  Tune,
  CreditCard,
  VisibilityOff,
  CalendarToday,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CONTEXTS } from 'figshelf-modules';
import { useLogoutBlocker } from '../contexts/LogoutBlockerContext';
import { clearClientCache } from '../utils/clearClientCache';
import { Logout as LogoutIcon } from '@mui/icons-material';

const SIDEBAR_WIDTH = 280;

const SettingsLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const auth = React.useContext(CONTEXTS.AuthContext);
  const { runLogout } = useLogoutBlocker();
  const queryClient = useQueryClient();

  const handleLogout = async () => {
    navigate('/', { replace: true });
    await runLogout(async () => {
      await clearClientCache({ queryClient });
      await auth.signOut();
    });
  };

  const settingsSections = [
    {
      title: 'Preferences',
      items: [
        { icon: <WorkspacePremium sx={{ color: '#FFD700' }} />, title: 'Premium', path: '/settings/premium' },
        { icon: <CreditCard />, title: 'Subscription', path: '/settings/subscription' },
        { icon: <Security />, title: 'Sign in & security', path: '/settings/security' },
        { icon: <Notifications />, title: 'Notifications', path: '/settings/notifications' },
        { icon: <AccountCircle />, title: 'Account Management', path: '/settings/account' },
        { icon: <Brightness4 />, title: 'Appearance', path: '/settings/appearance' },
        { icon: <Palette />, title: 'Color Theme', path: '/settings/theme' },
        { icon: <Tune />, title: 'Theme Customizer', path: '/settings/theme-customizer' },
      ],
    },
    {
      title: 'Audience and Visibility',
      items: [
        { icon: <AccountCircle />, title: 'Profile details', path: '/profile/details' },
        { icon: <People />, title: 'Followers/Followings', path: '/settings/followers' },
        { icon: <Block />, title: 'Blocking', path: '/settings/blocking' },
        { icon: <Group />, title: 'Groups', path: '/settings/groups' },
        { icon: <CloudDownload />, title: 'Backup downloads', path: '/settings/backup' },
        { icon: <Business />, title: 'Company settings', path: '/settings/company' },
        { icon: <LocalOffer />, title: 'Discount code', path: '/settings/discount' },
        { icon: <VisibilityOff />, title: 'Content Preferences', path: '/settings/content-preferences' },
        { icon: <CalendarToday />, title: 'Scheduled Posts', path: '/post/schedule' },
      ],
    },
    {
      title: 'Help',
      items: [
        { icon: <HeadsetMic />, title: 'Support', path: '/settings/support' },
      ],
    },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
          Settings
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate('/')}
          >
            <Home />
          </IconButton>
          {isMobile && (
            <IconButton onClick={handleDrawerToggle}>
              <ChevronLeft />
            </IconButton>
          )}
        </Box>
      </Box>
      <Divider />

      {/* Navigation */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 2 }}>
        {settingsSections.map((section, sectionIndex) => (
          <Box key={sectionIndex} sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              sx={{
                px: 3,
                py: 1,
                display: 'block',
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              {section.title}
            </Typography>
            <List disablePadding>
              {section.items.map((item, itemIndex) => {
                const isActive = isActivePath(item.path);
                return (
                  <ListItem key={itemIndex} disablePadding>
                    <ListItemButton
                      onClick={() => handleNavigation(item.path)}
                      sx={{
                        px: 3,
                        py: 1.5,
                        borderLeft: '3px solid',
                        borderColor: isActive ? 'primary.main' : 'transparent',
                        bgcolor: isActive ? 'action.selected' : 'transparent',
                        '&:hover': {
                          bgcolor: isActive ? 'action.selected' : 'action.hover',
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 40,
                          color: isActive ? 'primary.main' : 'text.secondary',
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.title}
                        primaryTypographyProps={{
                          fontSize: '0.9rem',
                          fontWeight: isActive ? 600 : 500,
                          color: isActive ? 'primary.main' : 'text.primary',
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* Logout */}
      <Divider />
      <Box sx={{ p: 2 }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 1,
            color: 'error.main',
            '&:hover': {
              bgcolor: 'error.light',
              color: 'error.contrastText',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{
              fontSize: '0.9rem',
              fontWeight: 600,
            }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Mobile Menu Button */}
      {isMobile && (
        <Box
          sx={{
            position: 'fixed',
            top: 70,
            left: 16,
            zIndex: 1200,
          }}
        >
          <IconButton
            onClick={handleDrawerToggle}
            sx={{
              bgcolor: 'background.paper',
              boxShadow: 2,
              '&:hover': {
                bgcolor: 'background.paper',
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      )}

      {/* Sidebar - Desktop */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            zIndex: 1100,
            '& .MuiDrawer-paper': {
              width: SIDEBAR_WIDTH,
              boxSizing: 'border-box',
              borderRight: '1px solid',
              borderColor: 'divider',
              top: 0,
              height: 'calc(100vh)',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Sidebar - Mobile */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: SIDEBAR_WIDTH,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${SIDEBAR_WIDTH}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
          overflow: 'auto',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default SettingsLayout;
